import { useEffect, useRef, useState } from "react";
import { CreditCard, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBillingIdentity } from "@/hooks/useBillingIdentity";
import { BillingIdentityModal, type BillingIdentity } from "@/components/settings/BillingIdentityModal";
import { DirectPaymentDialog } from "@/components/settings/DirectPaymentDialog";
import { formatCop } from "@/lib/credits";
import { useCreditTopupLimits } from "@/hooks/useCreditTopupLimits";
import {
  CREDIT_TOPUP_DESCRIPTION,
  creditTopupRefusal,
  MODE_CREDIT_TOPUP,
  parseCopInput,
  voiceTopupLimits,
} from "@/lib/credit-topup";
import { MODE_VOICE_TOPUP, VOICE_TOPUP_DESCRIPTION, voiceMinutesFor } from "@/lib/voice-credits";
import { useTopupWallets } from "@/hooks/useTopupWallets";
import { creditsDebtTopupCop, hasCreditsDebt, type CreditsDebt } from "@/lib/credits-debt";

const OTHER = "other" as const;
type Choice = number | typeof OTHER;
type Wallet = "chat" | "voice";

export function CreditTopupDialog({
  open,
  onOpenChange,
  orgId,
  payerEmail,
  onPaid,
  kind = "chat",
  voicePriceCop = null,
  askBillingIdentity = true,
  debt = null,
}: {
  kind?: Wallet;
  debt?: CreditsDebt | null;
  askBillingIdentity?: boolean;
  voicePriceCop?: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | null;
  payerEmail: string;
  onPaid?: () => void;
}) {
  const { identity, setIdentity, loaded: identityLoaded } = useBillingIdentity(orgId);
  const { limits: sharedLimits, loaded: limitsLoaded } = useCreditTopupLimits(open, open);
  const [wallet, setWallet] = useState<Wallet>(kind);
  const wallets = useTopupWallets(orgId, open);
  const limits = wallet === "voice" ? voiceTopupLimits(sharedLimits, wallets.voice?.topupMinCop ?? null) : sharedLimits;
  const firstPreset: Choice = limits.presetsCop[0] ?? OTHER;
  const firstPresetRef = useRef(firstPreset);
  firstPresetRef.current = firstPreset;
  const presetsKey = limits.presetsCop.join(",");
  const [choice, setChoice] = useState<Choice>(firstPreset);
  const [otherRaw, setOtherRaw] = useState("");
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payAmount, setPayAmount] = useState<number | null>(null);
  const [payWallet, setPayWallet] = useState<Wallet>(kind);
  const voicePrice = wallets.voice?.priceCop ?? voicePriceCop;
  const showSelector = wallets.voiceAvailable;
  const owesCredits = kind === "chat" && hasCreditsDebt(debt);
  const debtTopupCop = owesCredits && debt ? creditsDebtTopupCop(debt, limits) : null;
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (open) {
      setChoice(firstPresetRef.current);
      setOtherRaw("");
      setWallet(kind);
    }
  }, [open, kind]);

  useEffect(() => {
    if (wallets.loaded && !wallets.voiceAvailable) setWallet("chat");
  }, [wallets.loaded, wallets.voiceAvailable]);

  useEffect(() => {
    setChoice((current) => {
      if (current === OTHER) return current;
      const offered = presetsKey ? presetsKey.split(",").map(Number) : [];
      return offered.includes(current) ? current : offered[0] ?? OTHER;
    });
  }, [presetsKey]);

  useEffect(() => {
    if (!open) {
      prefilledRef.current = false;
      return;
    }
    if (prefilledRef.current || !limitsLoaded || debtTopupCop === null) return;
    prefilledRef.current = true;
    if (limits.presetsCop.includes(debtTopupCop)) {
      setChoice(debtTopupCop);
    } else {
      setChoice(OTHER);
      setOtherRaw(String(debtTopupCop));
    }
  }, [open, limitsLoaded, debtTopupCop, limits.presetsCop]);

  const amount = choice === OTHER ? parseCopInput(otherRaw) : choice;
  const refusal = creditTopupRefusal(amount, limits);
  const showOtherError = choice === OTHER && otherRaw.trim() !== "" && refusal;
  const isVoice = wallet === "voice";
  const topupMinutes = isVoice && !refusal ? voiceMinutesFor(amount, voicePrice) : null;
  const minutesLabel = topupMinutes !== null && voicePrice
    ? `≈ ${topupMinutes.toLocaleString("es-CO")} minutos a ${formatCop(voicePrice)}/min`
    : null;
  const voiceBalanceMinutes = wallets.voice ? voiceMinutesFor(wallets.voice.balanceCop, voicePrice) : null;

  const startPayment = () => {
    if (refusal || amount === null || !limitsLoaded || (wallet === "voice" && !wallets.loaded)) return;
    onOpenChange(false);
    setPayWallet(wallet);
    setPayAmount(amount);
    setPaymentOpen(true);
  };

  const handlePay = () => {
    if (!orgId) {
      toast.error("Selecciona un espacio de trabajo primero.");
      return;
    }
    if (askBillingIdentity && !identity?.billing_id_number) {
      setBillingModalOpen(true);
      return;
    }
    startPayment();
  };

  const handleIdentitySaved = (saved: BillingIdentity) => {
    setIdentity(saved);
    setBillingModalOpen(false);
    startPayment();
  };

  const optionClass = (selected: boolean) =>
    `rounded-xl border-2 p-3 text-left transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WalletIcon className="h-5 w-5 text-primary" /> Recargar saldo
            </DialogTitle>
            <DialogDescription>
              {isVoice
                ? `Tu saldo de voz paga los minutos de llamada de tus asistentes de voz, por segundo, a ${voicePrice ? formatCop(voicePrice) : "—"} por minuto. No vence.`
                : "Tu saldo paga las conversaciones que pasen el cupo de tu plan y tus conversaciones extras. No vence."}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {owesCredits && debt && wallet === "chat" && (
              <p className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/20" data-testid="topup-debt-hint">
                Tienes un saldo pendiente de {formatCop(debt.owedCop)} COP: esta recarga lo paga primero y el resto queda como saldo.
              </p>
            )}
            {showSelector && (
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Saldo a recargar" data-testid="topup-wallet-selector">
                {([
                  { id: "chat" as const, label: "Chat", detail: `Saldo ${formatCop(wallets.chatBalanceCop ?? 0)}` },
                  {
                    id: "voice" as const,
                    label: "Voz",
                    detail: `Saldo ${formatCop(wallets.voice?.balanceCop ?? 0)}${voiceBalanceMinutes !== null ? ` · ≈ ${voiceBalanceMinutes.toLocaleString("es-CO")} min` : ""}`,
                  },
                ]).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={wallet === option.id}
                    onClick={() => setWallet(option.id)}
                    className={optionClass(wallet === option.id)}
                  >
                    <span className="block text-sm font-semibold">{option.label}</span>
                    <span className="block text-xs text-muted-foreground tabular-nums">{option.detail}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {limits.presetsCop.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setChoice(preset)}
                  aria-pressed={choice === preset}
                  className={optionClass(choice === preset)}
                >
                  <span className="text-base font-semibold tabular-nums">{formatCop(preset)}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setChoice(OTHER)}
                aria-pressed={choice === OTHER}
                className={optionClass(choice === OTHER)}
              >
                <span className="text-base font-semibold">Otro</span>
              </button>
            </div>

            {choice === OTHER && (
              <div className="space-y-1">
                <Label htmlFor="credit-topup-other">Monto a recargar (COP)</Label>
                <Input
                  id="credit-topup-other"
                  inputMode="numeric"
                  autoFocus
                  placeholder={limits.minCop.toLocaleString("es-CO")}
                  value={otherRaw ? (parseCopInput(otherRaw) ?? 0).toLocaleString("es-CO") : ""}
                  onChange={(e) => setOtherRaw(e.target.value)}
                />
                <p className={`text-xs ${showOtherError ? "text-destructive" : "text-muted-foreground"}`}>
                  {showOtherError || `Entre ${formatCop(limits.minCop)} y ${formatCop(limits.maxCop)}, en pesos enteros.`}
                </p>
              </div>
            )}

            <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{isVoice ? "Saldo de voz que se suma" : showSelector ? "Saldo de chat que se suma" : "Saldo que se suma"}</span>
                <span className="tabular-nums">{refusal || amount === null ? "—" : `${formatCop(amount)} COP`}</span>
              </div>
              {isVoice && (
                <div className="flex items-center justify-between" data-testid="voice-topup-minutes">
                  <span className="text-muted-foreground">Equivale a</span>
                  <span className="tabular-nums">{minutesLabel ?? "—"}</span>
                </div>
              )}
              <div className="h-px bg-border" />
              <div className="flex items-center justify-between font-semibold">
                <span>Total a pagar</span>
                <span className="tabular-nums">{refusal || amount === null ? "—" : `${formatCop(amount)} COP`}</span>
              </div>
              <p className="text-xs text-muted-foreground">Pago único con tarjeta o PSE. No cambia tu plan ni tu suscripción.</p>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button className="w-full gap-1.5" onClick={handlePay} disabled={!!refusal || !identityLoaded || !limitsLoaded || (isVoice && !wallets.loaded)}>
              <CreditCard className="h-4 w-4" />
              {refusal || amount === null ? "Pagar" : `Pagar ${formatCop(amount)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {orgId && (
        <BillingIdentityModal
          open={billingModalOpen}
          orgId={orgId}
          initial={identity}
          onClose={() => setBillingModalOpen(false)}
          onSaved={handleIdentitySaved}
        />
      )}

      {orgId && payAmount !== null && (
        <DirectPaymentDialog
          open={paymentOpen}
          onOpenChange={(value) => {
            setPaymentOpen(value);
            if (!value) setPayAmount(null);
          }}
          orgId={orgId}
          amount={payAmount}
          description={payWallet === "voice" ? VOICE_TOPUP_DESCRIPTION : CREDIT_TOPUP_DESCRIPTION}
          payerEmail={payerEmail || undefined}
          mode={payWallet === "voice" ? MODE_VOICE_TOPUP : MODE_CREDIT_TOPUP}
          onSuccess={() => {
            toast.success(payWallet === "voice" ? "¡Pago aprobado! Tu saldo de voz se actualizará en unos segundos." : "¡Pago aprobado! Tu saldo se actualizará en unos segundos.");
            onPaid?.();
          }}
          onError={(err) => toast.error(err)}
        />
      )}
    </>
  );
}
